import {
  type CreateNotificationAudienceRequest,
  type CreateNotificationEventRequest,
  type CreateNotificationRuleRequest,
  type CreateNotificationTemplateRequest,
  type DryRunNotificationRuleRequest,
  type DryRunNotificationRuleResponse,
  DryRunNotificationRuleResponseSchema,
  type MyNotificationsInboxQuery,
  type MyNotificationsInboxResponse,
  MyNotificationsInboxResponseSchema,
  type MarkAllNotificationsReadResponse,
  MarkAllNotificationsReadResponseSchema,
  type NotificationAudienceItem,
  NotificationAudienceItemSchema,
  type EmailLogListQuery,
  type EmailLogListResponse,
  EmailLogListResponseSchema,
  type NotificationChannelConfigResponse,
  NotificationChannelConfigResponseSchema,
  type UpdateNotificationChannelConfigRequest,
  type VerifyNotificationChannelResponse,
  VerifyNotificationChannelResponseSchema,
  type NotificationAudienceListQuery,
  type NotificationAudienceListResponse,
  NotificationAudienceListResponseSchema,
  type NotificationChannelItem,
  NotificationChannelItemSchema,
  type NotificationChannelListResponse,
  NotificationChannelListResponseSchema,
  type NotificationEventItem,
  NotificationEventItemSchema,
  type NotificationEventListQuery,
  type NotificationEventListResponse,
  NotificationEventListResponseSchema,
  type NotificationItem,
  NotificationItemSchema,
  type NotificationListQuery,
  type NotificationListResponse,
  NotificationListResponseSchema,
  type NotificationRuleItem,
  NotificationRuleItemSchema,
  type NotificationRuleListQuery,
  type NotificationRuleListResponse,
  NotificationRuleListResponseSchema,
  type NotificationTemplateItem,
  NotificationTemplateItemSchema,
  type NotificationTemplateListQuery,
  type NotificationTemplateListResponse,
  NotificationTemplateListResponseSchema,
  type PreviewNotificationTemplateRequest,
  type PreviewNotificationTemplateResponse,
  PreviewNotificationTemplateResponseSchema,
  type PublishNotificationRequest,
  type TestSendNotificationRequest,
  type TestSendNotificationResponse,
  TestSendNotificationResponseSchema,
  type UpdateNotificationAudienceRequest,
  type UpdateNotificationChannelRequest,
  type UpdateNotificationEventRequest,
  type UpdateNotificationRuleRequest,
  type UpdateNotificationTemplateRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Notification engine API client. Mirrors the 24 endpoints exposed by
 * `apps/api/src/modules/notifications/notifications.controller.ts`,
 * grouped by concept area. Every list/detail response is validated with
 * the shared Zod schema so a server-side contract drift breaks at the
 * boundary (not later in a tab's render path).
 */

// ----- Events -----

export const listNotificationEvents = async (
  query: NotificationEventListQuery,
): Promise<NotificationEventListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/events', { params: query });
  return NotificationEventListResponseSchema.parse(data);
};

export const createNotificationEvent = async (
  payload: CreateNotificationEventRequest,
): Promise<NotificationEventItem> => {
  const { data } = await apiClient.post<unknown>('/notifications/events', payload);
  return NotificationEventItemSchema.parse(data);
};

export const updateNotificationEvent = async (
  id: string,
  payload: UpdateNotificationEventRequest,
): Promise<NotificationEventItem> => {
  const { data } = await apiClient.patch<unknown>(`/notifications/events/${id}`, payload);
  return NotificationEventItemSchema.parse(data);
};

export const deleteNotificationEvent = async (id: string): Promise<void> => {
  await apiClient.delete(`/notifications/events/${id}`);
};

// ----- Channels -----

export const listNotificationChannels = async (): Promise<NotificationChannelListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/channels');
  return NotificationChannelListResponseSchema.parse(data);
};

export const updateNotificationChannel = async (
  id: string,
  payload: UpdateNotificationChannelRequest,
): Promise<NotificationChannelItem> => {
  const { data } = await apiClient.patch<unknown>(`/notifications/channels/${id}`, payload);
  return NotificationChannelItemSchema.parse(data);
};

// ----- Templates -----

export const listNotificationTemplates = async (
  query: NotificationTemplateListQuery,
): Promise<NotificationTemplateListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/templates', { params: query });
  return NotificationTemplateListResponseSchema.parse(data);
};

export const createNotificationTemplate = async (
  payload: CreateNotificationTemplateRequest,
): Promise<NotificationTemplateItem> => {
  const { data } = await apiClient.post<unknown>('/notifications/templates', payload);
  return NotificationTemplateItemSchema.parse(data);
};

export const updateNotificationTemplate = async (
  id: string,
  payload: UpdateNotificationTemplateRequest,
): Promise<NotificationTemplateItem> => {
  const { data } = await apiClient.patch<unknown>(`/notifications/templates/${id}`, payload);
  return NotificationTemplateItemSchema.parse(data);
};

export const deleteNotificationTemplate = async (id: string): Promise<void> => {
  await apiClient.delete(`/notifications/templates/${id}`);
};

export const previewNotificationTemplate = async (
  id: string,
  payload: PreviewNotificationTemplateRequest,
): Promise<PreviewNotificationTemplateResponse> => {
  const { data } = await apiClient.post<unknown>(`/notifications/templates/${id}/preview`, payload);
  return PreviewNotificationTemplateResponseSchema.parse(data);
};

// ----- Audiences -----

export const listNotificationAudiences = async (
  query: NotificationAudienceListQuery,
): Promise<NotificationAudienceListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/audiences', { params: query });
  return NotificationAudienceListResponseSchema.parse(data);
};

export const createNotificationAudience = async (
  payload: CreateNotificationAudienceRequest,
): Promise<NotificationAudienceItem> => {
  const { data } = await apiClient.post<unknown>('/notifications/audiences', payload);
  return NotificationAudienceItemSchema.parse(data);
};

export const updateNotificationAudience = async (
  id: string,
  payload: UpdateNotificationAudienceRequest,
): Promise<NotificationAudienceItem> => {
  const { data } = await apiClient.patch<unknown>(`/notifications/audiences/${id}`, payload);
  return NotificationAudienceItemSchema.parse(data);
};

export const deleteNotificationAudience = async (id: string): Promise<void> => {
  await apiClient.delete(`/notifications/audiences/${id}`);
};

// ----- Rules -----

export const listNotificationRules = async (
  query: NotificationRuleListQuery,
): Promise<NotificationRuleListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/rules', { params: query });
  return NotificationRuleListResponseSchema.parse(data);
};

export const createNotificationRule = async (
  payload: CreateNotificationRuleRequest,
): Promise<NotificationRuleItem> => {
  const { data } = await apiClient.post<unknown>('/notifications/rules', payload);
  return NotificationRuleItemSchema.parse(data);
};

export const updateNotificationRule = async (
  id: string,
  payload: UpdateNotificationRuleRequest,
): Promise<NotificationRuleItem> => {
  const { data } = await apiClient.patch<unknown>(`/notifications/rules/${id}`, payload);
  return NotificationRuleItemSchema.parse(data);
};

export const deleteNotificationRule = async (id: string): Promise<void> => {
  await apiClient.delete(`/notifications/rules/${id}`);
};

export const dryRunNotificationRule = async (
  id: string,
  payload: DryRunNotificationRuleRequest,
): Promise<DryRunNotificationRuleResponse> => {
  const { data } = await apiClient.post<unknown>(`/notifications/rules/${id}/dry-run`, payload);
  return DryRunNotificationRuleResponseSchema.parse(data);
};

// ----- History -----

export const listNotificationHistory = async (
  query: NotificationListQuery,
): Promise<NotificationListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/history', { params: query });
  return NotificationListResponseSchema.parse(data);
};

/**
 * The user-facing "View all" listing. Server applies RBAC scope -
 * the response only contains notifications the current user is
 * allowed to see (ADMIN/OPS/FINANCE/READ_ONLY: org-wide;
 * SALES_LEAD: self + direct reports; everyone else: self only).
 */
export const listMyAllNotifications = async (
  query: NotificationListQuery,
): Promise<NotificationListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/me/all', { params: query });
  return NotificationListResponseSchema.parse(data);
};

export const retryNotification = async (id: string): Promise<void> => {
  await apiClient.post(`/notifications/history/${id}/retry`);
};

// ----- /me inbox (any authenticated user; powers the bell widget) -----

export const getMyNotificationsInbox = async (
  query: MyNotificationsInboxQuery,
): Promise<MyNotificationsInboxResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/me/inbox', { params: query });
  return MyNotificationsInboxResponseSchema.parse(data);
};

export const markNotificationRead = async (id: string): Promise<NotificationItem> => {
  const { data } = await apiClient.patch<unknown>(`/notifications/me/inbox/${id}/read`, {});
  return NotificationItemSchema.parse(data);
};

export const markAllNotificationsRead = async (): Promise<MarkAllNotificationsReadResponse> => {
  const { data } = await apiClient.post<unknown>('/notifications/me/inbox/mark-all-read', {});
  return MarkAllNotificationsReadResponseSchema.parse(data);
};

// ----- Escape hatches (QA) -----

export const publishNotificationEvent = async (
  payload: PublishNotificationRequest,
): Promise<unknown> => {
  const { data } = await apiClient.post<unknown>('/notifications/publish', payload);
  return data;
};

export const testSendNotification = async (
  payload: TestSendNotificationRequest,
): Promise<TestSendNotificationResponse> => {
  const { data } = await apiClient.post<unknown>('/notifications/test-send', payload);
  return TestSendNotificationResponseSchema.parse(data);
};

// ----- Email logs (legacy EmailDispatcher rows) -----

export const listNotificationEmailLogs = async (
  query: EmailLogListQuery,
): Promise<EmailLogListResponse> => {
  const { data } = await apiClient.get<unknown>('/notifications/email-logs', { params: query });
  return EmailLogListResponseSchema.parse(data);
};

// ----- Channel config (SMTP secrets) -----

export const getNotificationChannelConfig = async (
  code: string,
): Promise<NotificationChannelConfigResponse> => {
  const { data } = await apiClient.get<unknown>(`/notifications/channels/${code}/config`);
  return NotificationChannelConfigResponseSchema.parse(data);
};

export const updateNotificationChannelConfig = async (
  code: string,
  payload: UpdateNotificationChannelConfigRequest,
): Promise<unknown> => {
  const { data } = await apiClient.patch<unknown>(
    `/notifications/channels/${code}/config`,
    payload,
  );
  return data;
};

export const verifyNotificationChannel = async (
  code: string,
): Promise<VerifyNotificationChannelResponse> => {
  const { data } = await apiClient.post<unknown>(`/notifications/channels/${code}/verify`);
  return VerifyNotificationChannelResponseSchema.parse(data);
};
